import './inviteLinksGroupsChart.scss'
import clsx from 'clsx'
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Bar,
    BarChart,
} from 'recharts'
import { SelectDropdown } from '@/shared/ui'
// import { ChevronGreySoftIcon, GraphSVGIcon } from '@/shared/assets'
import { typedInviteLinksChartOptions } from '@/features/analytics/constants'
import { useInviteLinksStore } from '@/features/analytics/model'
import { useInviteLinksGroupsChartData } from './useInviteLinksGroupsChartData'
import { CustomLegend } from '@/features/analytics/components'
import { useGetGroupsByChannelIdQuery } from '@/shared/api'
import { ChevronGreySoftIcon, GraphSVGIcon } from '@/shared/assets'
import { useEffect, useMemo, useRef, useState } from 'react'

export type InviteLinksGroupsChartProps = {
    channelId: number
}
export const InviteLinksGroupsChart = ({
    channelId,
}: InviteLinksGroupsChartProps) => {
    const { data } = useGetGroupsByChannelIdQuery(channelId)
    const groups = data || []
    const {
        selectedChartMetric,
        chartIsExpanded,
        toggleChartIsExpanded,
        setSelectedChartMetric,
    } = useInviteLinksStore()
    const { inviteLinksData, inviteLinksGroups } =
        useInviteLinksGroupsChartData({ channelId, groups })
    const [selectedBarIndex, setSelectedBarIndex] = useState<number | null>(
        null
    )
    const [selectedPoint, setSelectedPoint] = useState<{
        x: string
        series: string
    } | null>(null)
    const chartRef = useRef<any>(null)
    const [yAxisDomain, setYAxisDomain] = useState<[number, number]>([0, 100])

    const yAxisValues = useMemo(
        () =>
            inviteLinksData.flatMap((d) =>
                Object.values(d).filter((v: any) => typeof v === 'number')
            ),
        [inviteLinksData]
    )

    const rawMaxY = useMemo(() => Math.max(...yAxisValues), [yAxisValues])

    useEffect(() => {
        const paddingPercentage = 0.2
        const maxY = getNiceMaxValue(rawMaxY, paddingPercentage)
        if (yAxisDomain[1] !== maxY) {
            setYAxisDomain([0, maxY])
        }
    }, [inviteLinksData, rawMaxY, yAxisDomain])

    // Function to preprocess the data
    const preprocessChartData = (data: any[]) => {
        if (data.length === 1) {
            const originalDate = new Date(data[0].date)
            const prevDate = new Date(originalDate)
            prevDate.setDate(originalDate.getDate() - 1) // One day before
            const nextDate = new Date(originalDate)
            nextDate.setDate(originalDate.getDate() + 1) // One day after

            // Create dummy entries with null values
            const dummyPrevEntry = {
                date: prevDate.toISOString(),
                ...Object.fromEntries(
                    Object.keys(data[0])
                        .filter((key) => key !== 'date')
                        .map((key) => [key, null])
                ),
            }
            const dummyNextEntry = {
                date: nextDate.toISOString(),
                ...Object.fromEntries(
                    Object.keys(data[0])
                        .filter((key) => key !== 'date')
                        .map((key) => [key, null])
                ),
            }

            // Return the modified dataset
            return [dummyPrevEntry, ...data, dummyNextEntry]
        }
        return data
    }

    // Inside the InviteLinksChart component
    const processedInviteLinksData = useMemo(
        () => preprocessChartData(inviteLinksData),
        [inviteLinksData]
    )

    const getMetricSign = (metric: string) => {
        if (metric === 'CTR' || metric === 'unsubscriptionPercentage') {
            return '%'
        } else if (
            metric === 'CPM' ||
            metric === 'CPS' ||
            metric === 'total_CPS' ||
            metric === 'budget'
        ) {
            return '₽'
        }
        return ''
    }

    const metricSign = getMetricSign(selectedChartMetric.value)

    function getNiceMaxValue(
        maxValue: number,
        paddingPercentage = 0.2
    ): number {
        if (maxValue <= 0) return 1

        const paddedMax = Math.round(maxValue + maxValue * paddingPercentage)
        let magnitude = Math.floor(Math.log10(paddedMax)) - 1
        magnitude = magnitude < 0 ? 0 : magnitude
        const factor = Math.pow(10, magnitude)
        const steps = magnitude >= 3 ? [1, 2, 5, 10] : [1, 2, 5]
        const scaledSteps = steps.map((step) => step * factor)

        for (const candidate of scaledSteps) {
            if (candidate > paddedMax) {
                return candidate
            }
        }

        return paddedMax
    }

    const CustomTooltip = ({ active, payload, label }: any) => {
        if (
            (active && payload && selectedBarIndex !== null) ||
            selectedPoint !== null
        ) {
            let filteredPayload
            if (selectedBarIndex) {
                filteredPayload = payload.filter(
                    (item: any) => item.dataKey === selectedBarIndex
                )
            } else if (selectedPoint) {
                filteredPayload = payload.filter(
                    (item: any) =>
                        item.dataKey === selectedPoint.series &&
                        new Date(label).toLocaleDateString('ru-RU') ===
                            selectedPoint.x
                )
            }
            return (
                <div
                    className="custom-tooltip"
                    style={{
                        backgroundColor: 'var(--white)',
                        border: '1px solid var(--stroke)',
                        borderRadius: '8px',
                        padding: '8px',
                        boxShadow: 'var(--box-shadow)',
                        fontSize: '12px',
                        fontFamily: 'Inter',
                    }}
                >
                    <p>
                        <strong>
                            {new Date(label).toLocaleDateString('ru-RU')}
                        </strong>
                    </p>
                    {filteredPayload.map((entry: any, index: number) => (
                        <p key={index}>
                            {entry.name}: {entry.value}
                            {metricSign}
                        </p>
                    ))}
                </div>
            )
        }
        return null
    }

    return (
        <section
            className={clsx('invite-links-group-chart', {
                'is-expanded': chartIsExpanded,
            })}
        >
            <div className="invite-links-group-chart__header">
                <div
                    className="invite-links-group-chart__icon-box"
                    onClick={toggleChartIsExpanded}
                >
                    <GraphSVGIcon />
                </div>
                <div className="invite-links-group-chart__controls">
                    <SelectDropdown
                        value={selectedChartMetric.value}
                        options={typedInviteLinksChartOptions}
                        onSelect={setSelectedChartMetric}
                        className="invite-links-group-chart__select"
                        headerClassName="invite-links-group-chart__select__header"
                        listClassName="invite-links-chart__select__list"
                    />
                    <button
                        onClick={toggleChartIsExpanded}
                        className="invite-links-group-chart__toggle-button"
                    >
                        {chartIsExpanded ? (
                            <span>Скрыть график</span>
                        ) : (
                            <span>Показать график</span>
                        )}
                        <img
                            src={ChevronGreySoftIcon}
                            className={clsx('select-dropdown__arrow', {
                                'is-open': chartIsExpanded,
                            })}
                            alt="Chevron Icon"
                        />
                    </button>
                </div>
            </div>

            <div className="invite-links-group-chart__container">
                {inviteLinksData.length === 0 ||
                inviteLinksGroups.length === 0 ? (
                    <div
                        style={{
                            display: 'flex',
                            position: 'absolute',
                            height: 'calc(100% - 92px)',
                            width: 'calc(100% - 28px)',
                            justifyContent: 'center',
                            alignItems: 'center',
                            fontSize: '13px',
                            color: 'var(--light-gray)',
                        }}
                    >
                        Нет данных
                    </div>
                ) : (
                    <>
                        {selectedChartMetric.value === 'budget' ? (
                            <ResponsiveContainer width="100%" height={350}>
                                <BarChart
                                    data={processedInviteLinksData}
                                    margin={{
                                        top: 5,
                                        right: 30,
                                        left: 20,
                                        bottom: 5,
                                    }}
                                >
                                    <CartesianGrid
                                        strokeDasharray="0 0"
                                        stroke="#e0e0e0"
                                        horizontal={true}
                                        vertical={true}
                                    />
                                    <XAxis
                                        dataKey="date"
                                        tickFormatter={(dateStr) =>
                                            new Date(
                                                dateStr
                                            ).toLocaleDateString('ru-RU')
                                        }
                                        label={{
                                            value: '',
                                            position: 'insideBottom',
                                            offset: -5,
                                            fill: 'var(--text-color)',
                                        }}
                                        tick={{
                                            fill: 'var(--text-color)',
                                            fontSize: 12,
                                        }}
                                        axisLine={false}
                                        tickLine={false}
                                    />
                                    <YAxis
                                        label={{
                                            value: selectedChartMetric.label,
                                            angle: -90,
                                            position: 'insideLeft',
                                            offset: 0,
                                            fill: 'var(--text-color)',
                                        }}
                                        tick={{
                                            fill: 'var(--text-color)',
                                            fontSize: 12,
                                        }}
                                        axisLine={false}
                                        tickLine={false}
                                    />
                                    <Tooltip
                                        cursor={{ fill: 'transparent' }}
                                        filterNull={true} // Hide tooltips for null or irrelevant data points
                                        formatter={(
                                            value?: number,
                                            name?: string
                                        ) => [`${value ?? ''}`, name]} // Format tooltip content
                                        // formatter={(value: number, name: string) => [
                                        //     `${value.toFixed(0)}`,
                                        //     `${name}`,
                                        // ]}
                                        contentStyle={{
                                            backgroundColor: 'var(--white)',
                                            border: '1px solid var(--stroke)',
                                            borderRadius: '8px',
                                            boxShadow: 'var(--box-shadow)',
                                        }}
                                        labelStyle={{
                                            color: 'var(--text-color)',
                                            fontWeight: 'bold',
                                        }}
                                        itemStyle={{
                                            color: 'var(--text-color)',
                                        }}
                                        active={selectedBarIndex !== null}
                                        content={<CustomTooltip />}
                                    />
                                    {inviteLinksGroups.map((link) => (
                                        <Bar
                                            key={link.id}
                                            dataKey={link.id}
                                            stackId={link.id}
                                            fill={link.color}
                                            name={link.name}
                                            barSize={Math.max(
                                                20,
                                                50 /
                                                    Math.max(
                                                        inviteLinksData.length,
                                                        1
                                                    )
                                            )}
                                            isAnimationActive={false}
                                            shape={{ stroke: 'none' }}
                                            onMouseEnter={() =>
                                                setSelectedBarIndex(link.id)
                                            }
                                            onMouseLeave={() =>
                                                setSelectedBarIndex(null)
                                            }
                                            label={({ x, y, value }) => (
                                                <text
                                                    x={Number(x) + 10}
                                                    y={y}
                                                    dy={-6} // Adjust vertical position of the label
                                                    fontSize={10}
                                                    fill={link.color}
                                                    textAnchor="middle"
                                                >
                                                    {value == 0
                                                        ? ''
                                                        : `${value ?? ''}${metricSign}`}
                                                </text>
                                            )}
                                        />
                                    ))}
                                    <CustomLegend
                                        payload={inviteLinksGroups}
                                        channelId={channelId}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        ) : (
                            <ResponsiveContainer width="100%" height={350}>
                                <LineChart
                                    ref={chartRef}
                                    data={inviteLinksData}
                                    margin={{
                                        top: 5,
                                        right: 30,
                                        left: 20,
                                        bottom: 5,
                                    }}
                                    onMouseMove={(e: any) => {
                                        if (
                                            e.activePayload &&
                                            e.activePayload.length > 0
                                        ) {
                                            const mouseX = e.activeCoordinate.x
                                            const mouseY = e.activeCoordinate.y

                                            const chartWidth =
                                                chartRef.current?.state?.offset
                                                    .width +
                                                    chartRef.current?.state
                                                        ?.offset.left || 783
                                            const chartHeight =
                                                chartRef.current?.state?.offset
                                                    .height +
                                                    chartRef.current?.state
                                                        ?.offset.top || 315

                                            const points = e.activePayload

                                            const xAxisRange =
                                                inviteLinksData.map((d) =>
                                                    new Date(d.date).getTime()
                                                )

                                            const minX = Math.min(...xAxisRange)
                                            const maxX = Math.max(...xAxisRange)
                                            const minY = yAxisDomain[0]
                                            const maxY = yAxisDomain[1]

                                            let closestPoint = points[0]
                                            let minDistance = Infinity
                                            points.forEach((point: any) => {
                                                const pointXValue = new Date(
                                                    point.payload.date
                                                ).getTime()
                                                const pointYValue =
                                                    point.payload[point.dataKey]

                                                const pointX =
                                                    ((pointXValue - minX) /
                                                        (maxX - minX)) *
                                                    chartWidth
                                                const pointY =
                                                    chartHeight -
                                                    ((pointYValue - minY) /
                                                        (maxY - minY)) *
                                                        chartHeight

                                                const distance = Math.sqrt(
                                                    Math.pow(
                                                        mouseX - pointX,
                                                        2
                                                    ) +
                                                        Math.pow(
                                                            mouseY - pointY,
                                                            2
                                                        )
                                                )

                                                if (distance < minDistance) {
                                                    minDistance = distance
                                                    closestPoint = point
                                                }
                                            })
                                            setSelectedPoint({
                                                x: new Date(
                                                    closestPoint.payload.date
                                                ).toLocaleDateString('ru-RU'),
                                                series: closestPoint.dataKey,
                                            })
                                        }
                                    }}
                                    onMouseLeave={() => setSelectedPoint(null)}
                                >
                                    <CartesianGrid
                                        strokeDasharray="0 0"
                                        stroke="#e0e0e0"
                                        // horizontal={true}
                                        // vertical={true}
                                    />
                                    <XAxis
                                        dataKey="date"
                                        tickFormatter={(dateStr) =>
                                            new Date(
                                                dateStr
                                            ).toLocaleDateString('ru-RU')
                                        }
                                        label={{
                                            value: '',
                                            position: 'insideBottom',
                                            offset: -5,
                                            fill: 'var(--text-color)',
                                        }}
                                        tick={{
                                            fill: 'var(--text-color)',
                                            fontSize: 12,
                                        }}
                                        axisLine={false}
                                        tickLine={false}
                                    />
                                    <YAxis
                                        label={{
                                            value: selectedChartMetric.label,
                                            angle: -90,
                                            position: 'insideLeft',
                                            offset: 0,
                                            fill: 'var(--text-color)',
                                        }}
                                        tick={{
                                            fill: 'var(--text-color)',
                                            fontSize: 12,
                                        }}
                                        axisLine={false}
                                        tickLine={false}
                                        domain={yAxisDomain}
                                    />
                                    <Tooltip
                                        labelFormatter={(dateStr) =>
                                            new Date(
                                                dateStr
                                            ).toLocaleDateString('ru-RU')
                                        }
                                        //  cursor={{fill: 'transparent'}}
                                        filterNull={true}
                                        formatter={(
                                            value?: number,
                                            name?: string
                                        ) => [`${value ?? ''}`, name]}
                                        // formatter={(value: number, name: string) => [
                                        //     `${value.toFixed(0)}`,
                                        //     `${name}`,
                                        // ]}
                                        contentStyle={{
                                            backgroundColor: 'var(--white)',
                                            border: '1px solid var(--stroke)',
                                            borderRadius: '8px',
                                            boxShadow: 'var(--box-shadow)',
                                        }}
                                        labelStyle={{
                                            color: 'var(--text-color)',
                                            fontWeight: 'bold',
                                        }}
                                        itemStyle={{
                                            color: 'var(--text-color)',
                                        }}
                                        active={selectedPoint !== null}
                                        content={<CustomTooltip />}
                                        // isAnimationActive={false}
                                        cursor={{ fill: 'rgba(0, 0, 0, 0.05)' }}
                                    />
                                    {inviteLinksGroups.map((link) => (
                                        <Line
                                            key={link.id}
                                            type="monotone"
                                            dataKey={link.id}
                                            stroke={link.color}
                                            name={link.name}
                                            label={({ x, y, value }) => (
                                                <text
                                                    x={x}
                                                    y={y}
                                                    dy={-6} // Adjust vertical position of the label
                                                    fontSize={10}
                                                    fill={link.color}
                                                    textAnchor="middle"
                                                >
                                                    {value == 0
                                                        ? ''
                                                        : `${value}${metricSign}`}
                                                </text>
                                            )}
                                            strokeWidth={1.5}
                                        />
                                    ))}
                                    <CustomLegend
                                        payload={inviteLinksGroups}
                                        channelId={channelId}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        )}
                        <CustomLegend
                            payload={inviteLinksGroups}
                            channelId={channelId}
                        />
                    </>
                )}
            </div>
        </section>
    )
}
